/**
 * "Your trusts" — the caller's own cross-firm role list.
 *
 * Wraps `GET /v1/me/trust-roles` (`backend/src/handlers/me-trust-roles-list.ts`),
 * which answers Beno's complaint from 2026-08-18: opening his profile did not
 * show a trust Waldo had registered, because a person's footprint was only ever
 * visible from inside the firm that happened to hold the deed. "We're a little
 * bit in a silo yet."
 *
 * ── Read the empty state carefully ───────────────────────────────────────────
 * `roles: []` means three different things, and the UI must not collapse them:
 *
 *   status 'identity-not-verified' — we have not confirmed who you are, so we
 *     will not look. Say that; do NOT say "you are on no trusts".
 *   status 'no-sa-id'             — verified, but the index is keyed on SA-ID
 *     numbers and you have none on file (a passport holder). Also not "none".
 *   status 'ok' with []            — the only case that genuinely means "your
 *     name does not appear on any trust on the platform".
 *
 * A 503 is a fourth case: the backend refuses to guess when the index or its key
 * is unavailable, precisely so the screen never shows a confident empty list
 * built on an infrastructure fault. TanStack surfaces it as an error, which is
 * the correct rendering.
 *
 * There is NO input to this call, by design — not even the caller's own ID
 * number. The backend derives the lookup key from the FICA-verified identity on
 * the user's own record; an ID number accepted over the wire would let anyone
 * enumerate a stranger's trusts. Do not add a parameter here.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from './client';

/** The three roles the cross-firm index carries. Narrower than the TPCA s1
 *  register enum: `settlor` is translated to `founder` server-side (the word an
 *  SA deed uses, and the word Beno uses), and `protector` / `other` are not
 *  indexed at all rather than being coerced into something they are not. */
export type PersonTrustRole = 'founder' | 'trustee' | 'beneficiary';

export interface TrustRoleView {
  tenantId: string;
  trustId: string;
  trustName: string;
  role: PersonTrustRole;
  /** Can this trust be opened right now? `false` is the interesting case — the
   *  name is in the deed but nobody has granted access yet. That row is where a
   *  future "claim this trust" action belongs. */
  hasAccess: boolean;
  /** When the role was first observed. Never restamped by a re-import. */
  addedAt: string;
  sourceDocumentId?: string;
}

export type MeTrustRolesStatus = 'ok' | 'identity-not-verified' | 'no-sa-id';

export interface MeTrustRolesResponse {
  identityVerified: boolean;
  status: MeTrustRolesStatus;
  roles: TrustRoleView[];
  correlationId: string;
}

export const ME_TRUST_ROLES_QUERY_KEY = ['me', 'trust-roles'] as const;

export function useMyTrustRoles(opts: { enabled?: boolean } = {}) {
  return useQuery<MeTrustRolesResponse>({
    queryKey: ME_TRUST_ROLES_QUERY_KEY,
    queryFn: ({ signal }) => api<MeTrustRolesResponse>('/api/v1/me/trust-roles', { signal }),
    enabled: opts.enabled ?? true,
  });
}

/** The order the profile screen groups by. Fixed rather than derived from the
 *  data so the headings do not reshuffle as rows arrive, and because it is the
 *  order a person describes their own involvement in: what I created, what I
 *  administer, what I receive. */
export const TRUST_ROLE_ORDER: readonly PersonTrustRole[] = [
  'founder',
  'trustee',
  'beneficiary',
] as const;

/** Plural heading for a role group. */
export function trustRoleGroupLabel(role: PersonTrustRole): string {
  switch (role) {
    case 'founder':
      return 'Trusts you founded';
    case 'trustee':
      return 'Trusts you are a trustee of';
    case 'beneficiary':
      return 'Trusts you are a beneficiary of';
  }
}

/** Group the flat rows by role, preserving `TRUST_ROLE_ORDER` and dropping
 *  empty groups. A person legitimately appears twice for one trust (founder AND
 *  trustee is the normal shape of a family trust), so this does NOT de-duplicate
 *  by trust — the whole question is "where am I what". */
export function groupRolesByKind(
  roles: readonly TrustRoleView[],
): Array<{ role: PersonTrustRole; label: string; rows: TrustRoleView[] }> {
  return TRUST_ROLE_ORDER.map((role) => ({
    role,
    label: trustRoleGroupLabel(role),
    rows: roles.filter((r) => r.role === role),
  })).filter((g) => g.rows.length > 0);
}
